import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Receipt,
  Trophy,
  Download,
  Printer,
  Users,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = '/api';

export function BetsReport() {
  const [races, setRaces] = useState([]);
  const [traders, setTraders] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('all');
  const [selectedTraderId, setSelectedTraderId] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);
  const { subscribeToType } = useWebSocket();

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      setRaces(response.data);
    } catch (error) {
      console.error('Failed to load races:', error);
    }
  };

  const fetchTraders = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setTraders(response.data.filter(u => u.role === 'trader'));
    } catch (error) {
      console.error('Failed to load traders:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/bets`;
      const params = [];
      if (selectedRaceId !== 'all') params.push(`race_id=${selectedRaceId}`);
      if (selectedTraderId !== 'all') params.push(`trader_id=${selectedTraderId}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRaces();
    fetchTraders();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [selectedRaceId, selectedTraderId]);

  // Real-time updates via WebSocket + polling fallback
  useEffect(() => {
    const unsubscribe = subscribeToType('*', (data) => {
      if (data.type === 'bet_placed' || data.type === 'bet_cancelled' || data.type === 'race_settled') {
        fetchReport();
        fetchRaces();
      }
    });

    const interval = setInterval(() => {
      fetchReport();
    }, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [subscribeToType, selectedRaceId, selectedTraderId]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMM');
    } catch {
      return dateStr;
    }
  };

  const getBetTypeColor = (betType) => {
    switch (betType) {
      case 'win': return 'bg-blue-500/20 text-blue-400';
      case 'place': return 'bg-emerald-500/20 text-emerald-400';
      case 'ew': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'text-emerald-400';
      case 'lost': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const exportCSV = () => {
    if (!report?.bets?.length) return;
    
    const headers = ['Race', 'Time', 'Runner #', 'Runner Name', 'Trader', 'Type', 'Odds', 'Stake', 'Potential Return', 'Status'];
    const rows = report.bets.map(bet => [
      bet.race_venue,
      bet.race_time,
      bet.runner_number,
      bet.runner_name,
      bet.trader_name || 'N/A',
      bet.bet_type?.toUpperCase(),
      bet.odds_display,
      bet.stake?.toFixed(2),
      bet.potential_payout?.toFixed(2),
      bet.status?.toUpperCase()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bets_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const printReport = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Bets Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f5f5f5; }
            .summary { margin-bottom: 20px; }
            .summary span { margin-right: 20px; }
          </style>
        </head>
        <body>
          <h1>Bets Report - ${new Date().toLocaleDateString()}</h1>
          <div class="summary">
            <span><strong>Total Bets:</strong> ${report?.summary?.total_bets || 0}</span>
            <span><strong>Total Stakes:</strong> €${report?.summary?.total_stakes?.toFixed(2) || '0.00'}</span>
            <span><strong>Win:</strong> ${report?.summary?.win_bets || 0}</span>
            <span><strong>Place:</strong> ${report?.summary?.place_bets || 0}</span>
            <span><strong>E/W:</strong> ${report?.summary?.ew_bets || 0}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Race</th>
                <th>Time</th>
                <th>#</th>
                <th>Runner</th>
                <th>Trader</th>
                <th>Type</th>
                <th>Odds</th>
                <th>Stake</th>
                <th>Return</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${report?.bets?.map(bet => `
                <tr>
                  <td>${bet.race_venue}</td>
                  <td>${bet.race_time}</td>
                  <td>${bet.runner_number}</td>
                  <td>${bet.runner_name}</td>
                  <td>${bet.trader_name || 'N/A'}</td>
                  <td>${bet.bet_type?.toUpperCase()}</td>
                  <td>${bet.odds_display}</td>
                  <td>€${bet.stake?.toFixed(2)}</td>
                  <td>€${bet.potential_payout?.toFixed(2)}</td>
                  <td>${bet.status?.toUpperCase()}</td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const selectedRace = races.find(r => r.id === selectedRaceId);

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="bets-report">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-zinc-100">Bets Report</h1>
            <p className="text-sm text-zinc-500">View and export all bets</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              className="border-zinc-700 text-zinc-400"
              onClick={exportCSV}
              disabled={!report?.bets?.length}
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="border-zinc-700 text-zinc-400"
              onClick={printReport}
              disabled={!report?.bets?.length}
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-zinc-500 flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Race
            </label>
            <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectValue>
                  {selectedRaceId === 'all' ? 'All Races' : (
                    <span>
                      {selectedRace?.venue} <span className="text-blue-400">{selectedRace?.time}</span>
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Races</SelectItem>
                {races.map(race => (
                  <SelectItem key={race.id} value={race.id}>
                    {race.venue} <span className="text-blue-400">{race.time}</span> - {formatDate(race.date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-500 flex items-center gap-1">
              <Users className="w-3 h-3" /> Trader
            </label>
            <Select value={selectedTraderId} onValueChange={setSelectedTraderId}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectValue>
                  {selectedTraderId === 'all' ? 'All Traders' : traders.find(t => t.id === selectedTraderId)?.username}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Traders</SelectItem>
                {traders.map(trader => (
                  <SelectItem key={trader.id} value={trader.id}>{trader.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        {report?.summary && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">Total Bets</p>
                <p className="font-mono text-lg font-bold text-zinc-100">{report.summary.total_bets}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">Stakes</p>
                <p className="font-mono text-lg font-bold text-emerald-400">€{report.summary.total_stakes?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">Win Bets</p>
                <p className="font-mono text-lg font-bold text-blue-400">{report.summary.win_bets}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">Place Bets</p>
                <p className="font-mono text-lg font-bold text-emerald-400">{report.summary.place_bets}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">E/W Bets</p>
                <p className="font-mono text-lg font-bold text-yellow-400">{report.summary.ew_bets}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-2 text-center">
                <p className="text-[9px] uppercase text-zinc-500">Potential</p>
                <p className="font-mono text-lg font-bold text-red-400">€{report.summary.total_potential_payout?.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bets Table */}
        <Card className="bg-zinc-900 border-zinc-800" ref={printRef}>
          <CardHeader className="py-3">
            <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-500" />
              Bets ({report?.bets?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-zinc-800 animate-pulse rounded" />
                ))}
              </div>
            ) : !report?.bets?.length ? (
              <div className="text-center py-8 text-zinc-500">
                <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No bets found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Race</TableHead>
                      <TableHead className="text-zinc-400">#</TableHead>
                      <TableHead className="text-zinc-400">Runner</TableHead>
                      <TableHead className="text-zinc-400">Trader</TableHead>
                      <TableHead className="text-zinc-400 text-center">Type</TableHead>
                      <TableHead className="text-zinc-400 text-right">Odds</TableHead>
                      <TableHead className="text-zinc-400 text-right">Stake</TableHead>
                      <TableHead className="text-zinc-400 text-right">Return</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.bets.map(bet => (
                      <TableRow key={bet.id} className="border-zinc-800">
                        <TableCell>
                          <div className="whitespace-nowrap">
                            <span className="font-medium text-zinc-100">{bet.race_venue}</span>
                            <span className="text-blue-400 ml-1">{bet.race_time}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-zinc-400">{bet.runner_number}</TableCell>
                        <TableCell className="text-zinc-100">{bet.runner_name}</TableCell>
                        <TableCell className="text-zinc-400">{bet.trader_name || 'N/A'}</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getBetTypeColor(bet.bet_type)}`}>
                            {bet.bet_type === 'ew' ? 'E/W' : bet.bet_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-zinc-100">{bet.odds_display}</TableCell>
                        <TableCell className="text-right font-mono text-zinc-100">€{bet.stake?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-400">€{bet.potential_payout?.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <span className={`text-xs font-bold uppercase ${getStatusColor(bet.status)}`}>
                            {bet.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
